import React, { useEffect, useState } from "react"
import clsx from "clsx"

import { usePermissions } from "contexts/PermissionsContext"
import { useProps } from "contexts/PropsContext"

import { PERMISSIONS } from "constants/permissions"

import { settingsService } from "services/settings/settings.service"

import { GgrScopeDetail } from "./GgrScopeDetail"
import { EMPTY_SCOPE, getPercent, parseGgrResponse, ScopeState } from "./utils"

const severityStyles = {
  critical: { border: "border-rose-500 text-rose-500", bg: "bg-rose-900/40", bgLight: "bg-rose-50" },
  warning: { border: "border-orange-400 text-orange-400", bg: "bg-orange-900/30", bgLight: "bg-orange-50" },
  healthy: { border: "border-emerald-500 text-emerald-500", bg: "bg-emerald-900/30", bgLight: "bg-emerald-50" }
}

export const GgrUsageBadge: React.FC = () => {
  const { darkMode } = useProps()
  const { has } = usePermissions()
  // GGR endpoints live under /admin/settings/* which is gated by PAGE_SETTINGS.
  // Skip the fetch entirely for users without that permission - otherwise the
  // badge renders on every page and 403s on every load.
  const canSeeGgr = has(PERMISSIONS.PAGE_SETTINGS)
  const [loading, setLoading] = useState(canSeeGgr)
  const [open, setOpen] = useState(false)

  const [casino, setCasino] = useState<ScopeState>(EMPTY_SCOPE)
  const [sportsbook, setSportsbook] = useState<ScopeState>(EMPTY_SCOPE)

  useEffect(() => {
    if (!canSeeGgr) return
    const load = async () => {
      setLoading(true)
      try {
        const [casinoRes, sportsbookRes] = await Promise.all([settingsService.getGgr(), settingsService.getSportsbookGgr()])
        setCasino(parseGgrResponse(casinoRes))
        setSportsbook(parseGgrResponse(sportsbookRes))
      } finally {
        setLoading(false)
      }
    }

    load()
  }, [canSeeGgr])

  if (!canSeeGgr) return null

  const casinoActive = casino.enabled && !!casino.snapshot
  const sportsbookActive = sportsbook.enabled && !!sportsbook.snapshot

  if (loading || (!casinoActive && !sportsbookActive)) {
    return null
  }

  const casinoPercent = getPercent(casino)
  const sportsbookPercent = getPercent(sportsbook)
  const maxPercent = Math.max(casinoActive ? casinoPercent : 0, sportsbookActive ? sportsbookPercent : 0)
  const anyLimitReached = casino.isLimitReached || sportsbook.isLimitReached

  const severity = maxPercent >= 90 ? "critical" : maxPercent >= 50 ? "warning" : "healthy"
  const s = severityStyles[severity]

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={clsx(
          "flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs font-medium whitespace-nowrap",
          darkMode ? "bg-[#21222E]" : "bg-[#F9FAFB]",
          darkMode ? s.bg : s.bgLight,
          s.border
        )}
        title="GGR usage for current month"
      >
        <span>GGR used</span>
        <span className="text-sm font-semibold">{maxPercent}%</span>
        {anyLimitReached && <span className="ml-1 text-[10px] uppercase">limit reached</span>}
      </button>

      {open && (
        <div
          className={clsx(
            "absolute right-0 mt-2 w-72 z-40 rounded-md border text-xs shadow-lg p-3 flex flex-col gap-3",
            darkMode ? "bg-[#21222E] text-white border-[rgba(255,255,255,0.1)]" : "bg-white text-black border-gray-200"
          )}
        >
          {casinoActive && (
            <GgrScopeDetail
              label="Casino"
              snapshot={casino.snapshot!}
              limit={casino.limit ?? 0}
              percent={casinoPercent}
              isLimitReached={casino.isLimitReached}
            />
          )}
          {casinoActive && sportsbookActive && <hr className="border-black/10 dark:border-white/10" />}
          {sportsbookActive && (
            <GgrScopeDetail
              label="Sportsbook"
              snapshot={sportsbook.snapshot!}
              limit={sportsbook.limit ?? 0}
              percent={sportsbookPercent}
              isLimitReached={sportsbook.isLimitReached}
            />
          )}
        </div>
      )}
    </div>
  )
}
